import { useBusinessContext } from '@context'
import { fetchDocument, IDocument } from '@query/documents'
import { IRelatedDocumentRole } from '@query/documentRelations'
import { useQueries } from '@tanstack/react-query'
import React from 'react'
import { Trans } from 'react-i18next'

import { DocumentRelationFormValue } from './RelationRow'
import {
  getAccrualLeadState,
  getLeadSentenceKey,
  getRelationFooterAmounts,
  getRelationRowAmount,
  getSettlementStatus,
  sumRelationAmounts
} from './relationMapperUtils'

type RelatedDocumentSummary = Pick<IDocument, 'balance' | 'relation_entry_signals'>

export function useRelatedDocumentsByRelation(
  relations: DocumentRelationFormValue[],
  slug: string | undefined
): {
  relatedDocumentsById: Record<number, RelatedDocumentSummary | undefined>
  isResolvingAmounts: boolean
} {
  const relatedDocumentQueries = useQueries({
    queries: relations.map(relation => ({
      queryKey: [slug, 'documents', relation.related_document],
      queryFn: () => fetchDocument({ documentId: relation.related_document, slug }),
      staleTime: 1000 * 60 * 5,
      enabled: Boolean(slug && relation.related_document)
    }))
  })

  const relatedDocumentsById: Record<number, RelatedDocumentSummary | undefined> = {}
  let isResolvingAmounts = false
  for (const [index, relation] of relations.entries()) {
    const query = relatedDocumentQueries[index]
    relatedDocumentsById[relation.related_document] = query?.data
    const amount = getRelationRowAmount(relation, query?.data)
    if (amount == null && (query?.isPending || query?.isFetching)) {
      isResolvingAmounts = true
    }
  }

  return { relatedDocumentsById, isResolvingAmounts }
}

export function useRelationLeadContent(
  relations: DocumentRelationFormValue[],
  {
    sourceAmount,
    openAmount,
    hasMismatch,
    networkInvoiceTotal = null,
    networkPaymentTotal = null,
    isFooterLoading = false
  }: {
    sourceAmount: number | null
    openAmount: number | null
    hasMismatch: boolean
    networkInvoiceTotal?: number | null
    networkPaymentTotal?: number | null
    isFooterLoading?: boolean
  }
): React.ReactNode | null {
  const { slug } = useBusinessContext()
  const currentRole = relations[0]?.role
  const { relatedDocumentsById, isResolvingAmounts } = useRelatedDocumentsByRelation(
    relations,
    slug
  )

  if (!currentRole || relations.length === 0) {
    return null
  }

  const linkedTotal = sumRelationAmounts(relations, relatedDocumentsById)
  const isSettlementRole = currentRole === IRelatedDocumentRole.SETTLEMENT
  const hasNetworkTotals = networkInvoiceTotal != null && networkPaymentTotal != null
  const { appliedAmount, targetAmount, liveOpenAmount } = getRelationFooterAmounts({
    role: currentRole,
    sourceAmount,
    linkedTotal,
    fallbackOpenAmount: openAmount,
    networkInvoiceTotal,
    networkPaymentTotal
  })
  const status = getSettlementStatus({
    invoiceAmount: targetAmount,
    paidAmount: appliedAmount,
    openAmount: liveOpenAmount,
    hasMismatch
  })
  const showFooterLoading = isFooterLoading || (isResolvingAmounts && !hasNetworkTotals)
  const leadState = isSettlementRole
    ? 'full'
    : getAccrualLeadState({
        status: showFooterLoading ? 'none' : status,
        appliedAmount,
        targetAmount
      })

  return (
    <Trans
      i18nKey={getLeadSentenceKey(currentRole, {
        leadState,
        relationType: relations[0].type
      })}
    />
  )
}
